/**
 * SEC-1 (T10) — fetch helper com JWT automático.
 *
 * Backend agora exige Authorization Bearer em quase todos os endpoints.
 * Em vez de espalhar `headers: { Authorization: ... }` em cada call site,
 * use authFetch() que injeta automaticamente o token de localStorage.
 *
 * Token storage key: "nexo_token" (mesma usada pelo useAuth).
 *
 * Uso:
 *   import { authFetch } from "@/lib/authFetch";
 *   const r = await authFetch(`${API_URL}/api/tasks`);
 *   const r = await authFetch(`${API_URL}/api/notes`, { method: "POST", body: JSON.stringify(...) });
 *
 * Sem token (logout/anônimo), envia request sem header — backend
 * retorna 401 e o caller decide o que fazer (logout, retry, etc).
 */

function _readToken() {
  try {
    return localStorage.getItem("nexo_token");
  } catch {
    return null;
  }
}

/**
 * Generate a short, unique-enough request id for correlating frontend
 * `[latency]` logs with backend `db.latency_events`. Browsers may not
 * expose crypto.randomUUID in non-secure contexts; fall back to time + random.
 */
export function newRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID().replace(/-/g, ""); } catch { /* fallthrough */ }
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * authFetch with optional X-Request-ID propagation.
 *
 * If `options.requestId` is provided, it is sent as `X-Request-ID` header
 * — matches the backend's middleware (server.py) which reads the same
 * header (or generates one) and stamps every measure() event with it.
 * Pass the generated id to your `[latency]` console.log calls for full
 * front-to-back correlation.
 */
export function authFetch(url, options = {}) {
  const token = _readToken();
  const baseHeaders = options.headers || {};
  const headers = token
    ? { ...baseHeaders, Authorization: `Bearer ${token}` }
    : { ...baseHeaders };
  if (options.requestId) {
    headers["X-Request-ID"] = options.requestId;
  }
  // Strip our custom field before passing to fetch (it doesn't accept it).
  const { requestId: _rid, ...fetchOptions } = options;
  void _rid;
  return fetch(url, { ...fetchOptions, headers });
}

export function authHeaders() {
  const token = _readToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
